import React from 'react';
import { TableRow, TableCell, Button, colors, Tooltip } from '@insurancemenu/imenu-components-lib';
import { CircleDown, CircleUp, Download, Reload, Skull } from '@insurancemenu/imenu-components-lib/dist/icons';
import { normDateToAmericanDateTime } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import {
  ExchangeFile,
  ExchangeFileStatus,
  FileExchangeDirection,
  FILE_EXCHANGE_TYPE_TITLES,
} from 'types/exchangeService';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { useRootStore } from 'store/rootStore';
import { createStyles } from './styles';

export interface FileExchangeRowProps {
  file: ExchangeFile;
}

const FileExchangeRow = ({ file }: FileExchangeRowProps): JSX.Element => {
  const { sftpPath, failCause, status, sponsorId, dateTime, id, direction } = file;
  const {
    fileExchangeStore: { openWarningModal, downloadFile },
  } = useRootStore();

  const isFileFailed = status === ExchangeFileStatus.FAILED;
  const classes = createStyles(isFileFailed);

  const FILE_EXCHANGE_TYPE_ICONS = Object.freeze({
    PULLED: <CircleDown color={colors.purple[700]} width="2.4rem" height="2.4rem" />,
    SENT: <CircleUp color={colors.green[600]} width="2.4rem" height="2.4rem" />,
    FAILED: <Skull color={colors.red[600]} width="2.4rem" height="2.4rem" />,
  });

  const Status = (
    <div className={classes.status}>
      <span>{FILE_EXCHANGE_TYPE_TITLES[status]}</span>
      {FILE_EXCHANGE_TYPE_ICONS[status]}
    </div>
  );

  return (
    <TableRow className={classes.tableRow}>
      <TableCell className={classes.tableCell}>{sponsorId}</TableCell>
      <TableCell className={classes.tableCell}>{normDateToAmericanDateTime(dateTime)}</TableCell>
      <TableCell className={classes.tableCell}>{sftpPath}</TableCell>
      <TableCell className={classes.tableCell}>
        {isFileFailed && failCause ? (
          <div className={classes.tooltip}>
            <Tooltip alignment={AlignmentTypes.BOTTOM_CENTER} label="Show error message">
              <Button
                color="custom"
                type="text"
                className={classes.errorButton}
                customColor={colors.red[600]}
                rightIconProp={FILE_EXCHANGE_TYPE_ICONS[status]}
                onClick={(): void => {
                  openWarningModal(file, 'SHOW_ERROR');
                }}
              >
                {FILE_EXCHANGE_TYPE_TITLES[status]}
              </Button>
            </Tooltip>
          </div>
        ) : (
          Status
        )}
      </TableCell>
      <TableCell className={classes.tableCell}>
        <Button
          leftIconProp={<Reload />}
          type="text"
          size="small"
          color="primary"
          className={classes.actionButton}
          disabled={direction === FileExchangeDirection.INBOUND}
          onClick={(): void => {
            openWarningModal(file, 'RESEND_FILE');
          }}
        >
          Re-send
        </Button>
      </TableCell>
      <TableCell className={classes.tableCell}>
        <Button
          leftIconProp={<Download />}
          type="text"
          size="small"
          color="primary"
          className={classes.actionButton}
          disabled={isFileFailed}
          onClick={(): void => {
            downloadFile(id);
          }}
        >
          Download
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default FileExchangeRow;
